import React, { ReactNode, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useMercuryContext } from "../../context";
import { ROUTES } from "../../common/constants/routes";
import { getFirstAccessibleGoogleRoute } from "../../apps/google/googleAccessRoutes";

interface ProtectedRouteProps {
  children: ReactNode;
  permissions?: {
    customerApplication: string;
    permissionName: string;
    action: "create" | "edit" | "delete" | "read";
  }[];
  requireAllPermissions?: boolean;
  dashboards?: {
    customerApplication: string;
    name: string;
  }[];
  adminCheck?: boolean;
  vendorManagerCheck?: boolean;
  flagCheck?: string[]; // list of Launch Darkly flag names that will return a boolean
  // Restricts the route to users who belong to at least one of the given customer applications.
  // Applied as an AND on top of the other access checks (adminCheck, flagCheck, etc.).
  customerApplication?: string | string[];
  redirectPath?: string;
}

interface IPermission {
  read: boolean;
  create: boolean;
  delete: boolean;
  edit: boolean;
  customerApplication: string;
  name: string;
  permissionId: string;
}

interface IDashboard {
  customerApplication: string;
  customerApplicationName?: string
  name: string;
}

interface ICustomerApplication {
  _id: string;
  name: string
  description: string;
}

// NOTE: need to pass at least one of the following props to enable the route
// permissions, adminCheck, vendorManagerCheck, flagCheck
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  permissions,
  requireAllPermissions = false,
  dashboards,
  adminCheck,
  vendorManagerCheck,
  flagCheck,
  customerApplication,
  redirectPath,
}) => {
  const { userDetails, flags, selectedApp } = useMercuryContext();
  const userPermissions: IPermission[] = userDetails?.permissions || [];
  let assignedDashboards: IDashboard[] = userDetails?.assignedDashboards || [];
  const customerApplicationDetails: ICustomerApplication[] = userDetails?.customerApplicationDetails || [];
  const selectedCustomerApplicationName = customerApplicationDetails.find(
    (customerApplication) => customerApplication._id === selectedApp
  )?.name;

  const googleFallbackRoute = useMemo(() => {
    return getFirstAccessibleGoogleRoute(userPermissions, userDetails);
  }, [userPermissions, userDetails]);

  // default denied-route fallback should land users on their selected customer application home
  const selectedAppFallbackPath = (() => {
    if (selectedCustomerApplicationName === "Google") {
      return googleFallbackRoute;
    }

    if (selectedCustomerApplicationName === "OLiVE") {
      return ROUTES.OLIVE_MAIN;
    }

    return ROUTES.UNAUTHORIZED;
  })();

  const rdPath = redirectPath ? `/${redirectPath}` : selectedAppFallbackPath;

  // Add the names of the customer applicaitons the user has access to, to the dashboards they have access to
  assignedDashboards = assignedDashboards.map((assignedDashboard) => {
    assignedDashboard.customerApplicationName = customerApplicationDetails.find(customerApplication => customerApplication._id === assignedDashboard.customerApplication)?.name;
    return assignedDashboard;
  });

  // --- Permission check (only if permissions are provided) ---
  const hasPermission =
    Array.isArray(permissions) &&
    (requireAllPermissions
      ? permissions.every((requiredPermission) =>
          userPermissions.some(
            (perm) =>
              perm.name === requiredPermission.permissionName &&
              perm[requiredPermission.action] === true &&
              perm.customerApplication === requiredPermission.customerApplication
          )
        )
      : permissions.some((requiredPermission) =>
          userPermissions.some(
            (perm) =>
              perm.name === requiredPermission.permissionName &&
              perm[requiredPermission.action] === true &&
              perm.customerApplication === requiredPermission.customerApplication
          )
        ));

  // Dashboard check (only if dashboards are provided)
  const hasDashboard = 
    Array.isArray(dashboards) &&
    dashboards.some((requiredDashboard) =>
      assignedDashboards.some(
        (dashboard) => {
          return dashboard.name === requiredDashboard.name &&
          dashboard.customerApplicationName === requiredDashboard.customerApplication
        }
      )
        
    )

  // --- Admin and Vendor Manager checks ---
  const isAdmin = adminCheck && userDetails?.admin === true;
  const isVendorManager =
    vendorManagerCheck && userDetails?.vendorManager === true;

  // --- Flag check ---
  const hasFlag =
    Array.isArray(flagCheck) &&
    flagCheck.some((flagName) => flags?.[flagName] === true);

  // --- Customer application scope (AND-gate) ---
  // When customerApplication is provided, the user must belong to at least one of
  // the listed CAs. This prevents e.g. an admin without Mercury CA from reaching
  // Mercury-only admin routes via direct URL.
  const requiredCustomerApplications = Array.isArray(customerApplication)
    ? customerApplication
    : customerApplication
      ? [customerApplication]
      : [];
  const hasCustomerApplication =
    requiredCustomerApplications.length === 0 ||
    requiredCustomerApplications.some((name) =>
      customerApplicationDetails.some((ca) => ca.name === name)
    );

  // --- Access allowed if ANY condition passes AND the CA scope (if any) matches ---
  const hasAccess =
    hasCustomerApplication &&
    ((!!permissions && hasPermission) || (!!dashboards && hasDashboard) || isAdmin || isVendorManager || hasFlag);

  if (!hasAccess) {
    return <Navigate to={rdPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
